import { DateExclusionGroup, RichItem, ShippingRate, Vendor } from '../carrierProcessor/types.js';

export const estimateDeliveryDates = ({
  shippingRates,
  richItems,
  vendors,
  dateExclusionGroups,
  now
}: {
  shippingRates: Map<string, ShippingRate>;
  richItems: Map<string, RichItem>;
  vendors: Map<string, Vendor>;
  dateExclusionGroups: Map<string, DateExclusionGroup>;
  now: Date;
}): Map<string, Map<string, Date>> => {
  // Loop through each shipping rate and estimate the delivery dates for each variant
  const allEstimatedDeliveryDates = new Map<string, Map<string, Date>>();
  for (const [rateId, rate] of shippingRates) {
    const { leadDays: rateLeadDays = 0 } = rate;
    const rateEstimatedDeliveryDates = new Map<string, Date>();

    // Grab all unique excluded dates and weekdays for the shipping rate
    const dateExclusions = {
      excludedDates:
        rate.dateExclusionGroupIds
          ?.map((dateExclusionGroupId) => {
            return dateExclusionGroups.get(dateExclusionGroupId)?.excludedDates || [];
          })
          .flat() || [],
      excludedWeekdays:
        rate.dateExclusionGroupIds
          ?.map((dateExclusionGroupId) => {
            return dateExclusionGroups.get(dateExclusionGroupId)?.excludedWeekdays || [];
          })
          .flat() || []
    };

    // Loop through each item and calculate its delivery date
    for (const [variantId, variant] of richItems) {
      const { leadDays: itemLeadDays = 0, vendorId } = variant;
      const vendor = vendors.get(`${vendorId}`);
      const vendorHasRate = vendor?.eligibleRateIds?.some((eligibleRateId) => eligibleRateId === rateId);
      const isPreorder = variant.preorderActive && variant.preorderDate && variant.preorderDate >= new Date();

      if (!vendorHasRate) {
        continue;
      }

      // Grab all unique excluded dates and weekdays for the vendor
      const vendorDateExclusions = {
        excludedDates:
          vendor?.dateExclusionGroupIds
            ?.map((dateExclusionGroupId) => {
              return dateExclusionGroups.get(dateExclusionGroupId)?.excludedDates || [];
            })
            .flat() || [],
        excludedWeekdays:
          vendor?.dateExclusionGroupIds
            ?.map((dateExclusionGroupId) => {
              return dateExclusionGroups.get(dateExclusionGroupId)?.excludedWeekdays || [];
            })
            .flat() || []
      };

      const estimatedDate = calculateDeliveryDate({
        variantLeadDays: itemLeadDays,
        vendorLeadDays: vendor?.leadDays || 0,
        shippingRateLeadDays: rateLeadDays,
        cutoffTime: rate.cutoffTime,
        cutoffTimezone: rate.cutoffTimezone,
        dateExclusions,
        vendorDateExclusions,
        isPreorder: Boolean(isPreorder),
        preorderDate: variant.preorderDate?.toISOString(),
        now
      });

      rateEstimatedDeliveryDates.set(variantId, estimatedDate);
    }
    allEstimatedDeliveryDates.set(rateId, rateEstimatedDeliveryDates);
  }
  return allEstimatedDeliveryDates;
};

type DateExclusions = {
  excludedDates: string[];
  excludedWeekdays: number[];
};

type CalculateDeliveryDateInput = {
  variantLeadDays: number;
  vendorLeadDays: number;
  shippingRateLeadDays: number;
  cutoffTime?: string;
  cutoffTimezone?: 'SHOP' | 'DESTINATION';
  dateExclusions: DateExclusions;
  vendorDateExclusions: DateExclusions;
  isPreorder: boolean;
  preorderDate?: string;
  now: Date;
};

export function calculateDeliveryDate(input: CalculateDeliveryDateInput): Date {
  const {
    variantLeadDays,
    vendorLeadDays,
    shippingRateLeadDays,
    dateExclusions,
    vendorDateExclusions,
    isPreorder,
    preorderDate,
    cutoffTime,
    now
  } = input;

  // Extract excludedWeekdays and dates from dateExclusions (for shipping rate).
  const excludedWeekdays: number[] = dateExclusions?.excludedWeekdays || [];
  const exclusionDates: string[] = dateExclusions?.excludedDates || [];

  // Extract excludedWeekdays and dates from vendorDateExclusions.
  const vendorExcludedWeekdays: number[] = vendorDateExclusions?.excludedWeekdays || [];
  const vendorExclusionDates: string[] = vendorDateExclusions?.excludedDates || [];

  // Handle preorder case
  if (isPreorder && preorderDate) {
    const date = new Date(preorderDate);
    // Start from next day after preorder date
    date.setDate(date.getDate() + 1);

    let remainingDays = shippingRateLeadDays;
    while (remainingDays > 0) {
      const weekday = date.getDay();
      const isoDate = date.toISOString().slice(0, 10);
      // Skip if excluded weekday or date
      if (!excludedWeekdays.includes(weekday) && !exclusionDates.includes(isoDate)) {
        remainingDays--;
      }
      date.setDate(date.getDate() + 1);
    }
    return date;
  }

  // Handle regular case
  let date;
  if (now) {
    date = new Date(now);
  } else {
    date = new Date();
  }
  date.setDate(date.getDate());

  // Add another day if after cutoff
  if (cutoffTime) {
    const [cutoffHour, cutoffMinute] = cutoffTime.split(':').map(Number);
    const currentHourGMT = date.getUTCHours();
    const currentMinuteGMT = date.getUTCMinutes();
    const afterCutoff =
      currentHourGMT > cutoffHour || (currentHourGMT === cutoffHour && currentMinuteGMT >= cutoffMinute);
    if (afterCutoff) {
      date.setDate(date.getDate() + 1);
    }
  }

  // Process variant lead days, excluding Sundays and vendor-specific excluded dates/weekdays
  let remainingVariantDays = variantLeadDays;
  while (remainingVariantDays > 0) {
    date.setDate(date.getDate() + 1);
    const weekday = date.getDay();
    const isoDate = date.toISOString().slice(0, 10);
    
    // Skip if it's Sunday, vendor excluded weekday, or vendor excluded date
    if (weekday !== 0 && !vendorExcludedWeekdays.includes(weekday) && !vendorExclusionDates.includes(isoDate)) {
      remainingVariantDays--;
    }
  }

  // Process vendor lead days, excluding Sundays and vendor-specific excluded dates/weekdays.
  let remainingVendorDays = vendorLeadDays;
  while (remainingVendorDays > 0) {
    date.setDate(date.getDate() + 1);
    const weekday = date.getDay();
    const isoDate = date.toISOString().slice(0, 10);

    // Skip if it's Sunday, vendor excluded weekday, or vendor excluded date.
    if (weekday !== 0 && !vendorExcludedWeekdays.includes(weekday) && !vendorExclusionDates.includes(isoDate)) {
      remainingVendorDays--;
    }
  }

  // Process shipping rate lead days, excluding weekends and excluded dates/weekdays
  let remainingShippingDays = shippingRateLeadDays;
  while (remainingShippingDays > 0) {
    date.setDate(date.getDate() + 1);
    const weekday = date.getDay();
    const isoDate = date.toISOString().slice(0, 10);

    // Skip if it's an excluded weekday, or excluded date
    if (!excludedWeekdays.includes(weekday) && !exclusionDates.includes(isoDate)) {
      remainingShippingDays--;
    }
  }

  // Ensure final date isn't on an excluded weekday or date
  while (true) {
    const weekday = date.getDay();
    const isoDate = date.toISOString().slice(0, 10);

    if (excludedWeekdays.includes(weekday) || exclusionDates.includes(isoDate)) {
      date.setDate(date.getDate() + 1);
      continue;
    }
    break;
  }

  return date;
}
